import { useState, useEffect } from "react";
import { api } from "../Contexts/AuthContext";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faLayerGroup, 
  faPlus, 
  faEdit, 
  faTrash, 
  faCube,
  faGlobe,
  faScrewdriverWrench,
  faCircleCheck,
  faInfoCircle,
  faObjectGroup,
  faSignature,
  faFileContract
} from "@fortawesome/free-solid-svg-icons";
import "../styles/admin_revamp.css";

export default function AdminWorkspaces() {
  const [templates, setTemplates] = useState([]);
  const [modules, setModules] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    name: "", description: "", industries: [], default_modules: []
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templRes, modRes, indRes] = await Promise.all([
        api.get("workspaces/templates/"),
        api.get("workspaces/modules/"),
        api.get("organizations/industries/")
      ]);
      setTemplates(templRes.data);
      setModules(modRes.data);
      setIndustries(indRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`workspaces/templates/${editId}/`, formData);
        toast.success("Blueprint updated");
      } else {
        await api.post("workspaces/templates/", formData);
        toast.success("New blueprint architected");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error("Architecture failure");
    }
  };

  const toggleItem = (listName, id) => {
    const list = [...formData[listName]];
    setFormData({ ...formData, [listName]: list.includes(id) ? list.filter(i => i !== id) : [...list, id] });
  };

  if (loading) return (
    <div className="admin-revamp-container text-center py-5">
      <div className="stat-icon-box" style={{margin: '0 auto 20px', color: '#799F0C'}}>⌛</div>
      <h2>Architecting Blueprint Matrix...</h2>
    </div>
  );

  return (
    <div className="admin-revamp-container">
      <div className="admin-header">
        <div className="admin-header-text">
          <h1>Workspace Blueprints</h1>
          <p>Define reusable infrastructure packages for platform industries.</p>
        </div>
        <button className="btn-admin-primary" onClick={() => {
          setFormData({ name: "", description: "", industries: [], default_modules: [] });
          setEditId(null);
          setShowModal(true);
        }}>
          <FontAwesomeIcon icon={faPlus} />
          <span>New Blueprint</span>
        </button>
      </div>

      <div className="admin-stats-strip">
        <div className="stat-mini-card">
          <div className="stat-icon-box"><FontAwesomeIcon icon={faLayerGroup} /></div>
          <div className="stat-info">
            <span className="stat-label">Total Blueprints</span>
            <span className="stat-value">{templates.length}</span>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-icon-box"><FontAwesomeIcon icon={faCube} /></div>
          <div className="stat-info">
            <span className="stat-label">Core Modules</span>
            <span className="stat-value">{modules.length}</span>
          </div>
        </div>
      </div>

      <div className="admin-tile-grid">
        {templates.map(templ => (
          <div key={templ.id} className="admin-tile">
            <div className="tile-header">
              <div className="tile-icon-orb"><FontAwesomeIcon icon={faObjectGroup} /></div>
              <span className="tag-pill tag-lime">{templ.default_modules?.length} Features</span>
            </div>
            <div className="tile-content">
              <h3>{templ.name}</h3>
              <p style={{fontSize: '13px', color: '#64748b', marginBottom: '16px'}}>{templ.description || "Foundational template structure."}</p>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                {templ.industry_names?.map(ind => (
                  <span key={ind} className="tag-pill tag-emerald">{ind}</span>
                ))}
              </div>
            </div>
            <div className="tile-footer">
              <button className="btn-tile-action primary" onClick={() => {
                  setFormData({ 
                    name: templ.name, description: templ.description, 
                    industries: templ.industries, default_modules: templ.default_modules 
                  });
                  setEditId(templ.id);
                  setShowModal(true);
              }}>
                <FontAwesomeIcon icon={faEdit} /> Configure
              </button>
              <button className="btn-tile-action danger" onClick={async () => {
                   if (window.confirm("Decommission this blueprint?")) {
                     await api.delete(`workspaces/templates/${templ.id}/`);
                     fetchData();
                   }
              }}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-revamp-overlay">
          <div className="modal-revamp-content" style={{maxWidth: '850px'}}>
            <div className="modal-revamp-header">
              <h2>{editId ? "Modify Blueprint" : "Architect New Blueprint"}</h2>
            </div>
            <form onSubmit={handleSave}>
              <div className="revamp-form-group">
                <label>Blueprint Identifier</label>
                <div className="revamp-input-wrapper">
                  <FontAwesomeIcon icon={faSignature} />
                  <input className="revamp-input" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Core Healthcare v2" />
                </div>
              </div>
              <div className="revamp-form-group">
                <label>Blueprint Description</label>
                <div className="revamp-input-wrapper">
                  <FontAwesomeIcon icon={faFileContract} style={{top: '18px'}} />
                  <textarea className="revamp-input" rows="2" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} style={{paddingLeft: '44px'}} placeholder="Define the scope of this infrastructure package..." />
                </div>
              </div>
              
              <div style={{display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '32px', marginTop: '32px'}}>
                <div>
                  <label style={{fontSize: '11px', fontWeight: '800', color: '#799F0C', display: 'block', marginBottom: '16px', textTransform: 'uppercase'}}>Target Verticals</label>
                  <div className="revamp-module-grid" style={{gridTemplateColumns: '1fr'}}>
                    {industries.map(ind => (
                      <div key={ind.id} className={`module-chip ${formData.industries.includes(ind.id) ? 'selected' : ''}`} onClick={() => toggleItem('industries', ind.id)}>
                        <FontAwesomeIcon icon={faGlobe} />
                        <span>{ind.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{fontSize: '11px', fontWeight: '800', color: '#799F0C', display: 'block', marginBottom: '16px', textTransform: 'uppercase'}}>Provisioned Features</label>
                  <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                    {Object.entries(modules.reduce((acc, mod) => {
                      const cluster = mod.tag || "General";
                      if (!acc[cluster]) acc[cluster] = [];
                      acc[cluster].push(mod);
                      return acc;
                    }, {})).map(([cluster, clusterModules]) => (
                      <div key={cluster} style={{ marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#cbd5e1', marginBottom: '10px' }}>{cluster}</div>
                        <div className="revamp-module-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
                          {clusterModules.map(mod => (
                            <div key={mod.id} className={`module-chip ${formData.default_modules.includes(mod.id) ? 'selected' : ''}`} onClick={() => toggleItem('default_modules', mod.id)}>
                              {mod.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="modal-revamp-footer">
                <button type="button" className="btn-revamp-ghost" onClick={() => setShowModal(false)}>Discard</button>
                <button type="submit" className="btn-revamp-solid" style={{background: '#799F0C'}}>Deploy Blueprint</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
